/**
 * StepView's INSTRUCTIONS and INTERIM_RESULTS dialogs: open state, the
 * show/hide handlers, the instructions-on-ready rule and the end-results rule
 * (moved verbatim from StepView.vue).
 *
 * Registers, IN THIS ORDER, when called:
 *  1. watch isStackReady (immediate) → showInfo() once, after a nextTick, never
 *     for a completed or skipped step
 *  2. watchEffect                    → completed + show_end_results opens the
 *                                      results dialog (closes instructions)
 *  3. watch stepTypeCamelCase (immediate) → prefetch the INSTRUCTIONS info-card
 *     chunk for that kind, so it is warm before the dialog opens (P4-2/W2a)
 * Call it AFTER useStepViewLifecycle (step:complete emit watcher first) and
 * BEFORE useStepHeaderActions (header-actions watchEffect last).
 */
import {
  computed,
  nextTick,
  provide,
  ref,
  watch,
  watchEffect,
  type Ref,
} from 'vue';
import type { StepTypeCamelCase } from 'src/types/course/step';
import { useStepChunkPrefetch } from './useStepChunkPrefetch';
import { STEP_QUESTION_CONCEALED } from '../components/StepCard/stepCardKeys';

type ReadRef<T> = Readonly<Ref<T>>;

export interface UseStepDialogsOptions {
  isStackReady: ReadRef<boolean>;
  isCompletedStep: ReadRef<boolean>;
  isSkippedStep: ReadRef<boolean>;
  stepDrillProps: ReadRef<
    { drillSettings?: { show_end_results?: boolean | null } | null } | undefined
  >;
  timerStart: () => void;
  timerStop: () => void;
  playQuestion: () => Promise<unknown>;
  /** The raw step kind (Theory → its own info card, any other → default). */
  stepTypeCamelCase?: ReadRef<StepTypeCamelCase | null | undefined>;
  /** Speed Round: the drill has a time limit (useDrillTimeLimit). */
  isTimeLimited?: ReadRef<boolean>;
}

export interface UseStepDialogsReturn {
  isInfoDialog: Ref<boolean>;
  isInterimResultsDialog: Ref<boolean>;
  showInfo: () => void;
  viewResults: () => void;
  infoDialogHideHandler: () => void;
  interimResultsDialogHideHandler: () => void;
  /** A dialog paused a time-limited try: the question is hidden. */
  isQuestionConcealed: Readonly<Ref<boolean>>;
}

export function useStepDialogs(
  opts: UseStepDialogsOptions,
): UseStepDialogsReturn {
  const { isStackReady, isCompletedStep, isSkippedStep, stepDrillProps } = opts;
  const { timerStart, timerStop, playQuestion } = opts;

  const isInfoDialog = ref(false);
  const isInterimResultsDialog = ref(false);

  // Speed Round: the clock is paused while these dialogs are open, so the
  // question must not be readable behind them (StepCard hides it).
  const isQuestionConcealed = computed(
    () =>
      !!opts.isTimeLimited?.value &&
      !isCompletedStep.value &&
      (isInfoDialog.value || isInterimResultsDialog.value),
  );
  provide(STEP_QUESTION_CONCEALED, isQuestionConcealed);

  // Instructions open once, when the drill is ready — for a filter step only
  // after the filter answer, so a skipped filter never flashes them.
  let infoShownOnReady = false;
  watch(
    () => isStackReady.value,
    async (ready) => {
      if (!ready || infoShownOnReady) return;
      infoShownOnReady = true;
      await nextTick();
      if (!isCompletedStep.value && !isSkippedStep.value) showInfo();
    },
    { immediate: true },
  );

  function infoDialogHideHandler() {
    if (isCompletedStep.value) return;
    // Same try continues (paused while the dialog was open); its speech counts.
    timerStart();
    playQuestion().catch((error) => {
      console.error('Error playing question after info dialog hide:', error);
    });
  }

  const viewResults = () => {
    isInfoDialog.value = false;

    // Stop timer
    timerStop();

    isInterimResultsDialog.value = true;
  };

  function interimResultsDialogHideHandler() {
    isInterimResultsDialog.value = false;

    if (isCompletedStep.value) return;

    // Same try continues (paused while the dialog was open); its speech counts.
    timerStart();
    playQuestion().catch((error) => {
      console.error('Error playing question after results dialog hide:', error);
    });
  }

  function showInfo() {
    // Implement show information logic
    isInterimResultsDialog.value = false;

    // Stop timer
    timerStop();

    // Show info dialog
    isInfoDialog.value = true;
  }

  watchEffect(() => {
    if (
      isCompletedStep.value &&
      stepDrillProps.value?.drillSettings?.show_end_results
    ) {
      isInfoDialog.value = false;
      isInterimResultsDialog.value = true;
    }
  });

  // Warm the info card's chunk as soon as the kind is known (the step GET),
  // well before the stack is ready and the dialog opens. Prefetch only.
  const { prefetchInfoForKind } = useStepChunkPrefetch();
  watch(
    () => opts.stepTypeCamelCase?.value,
    (kind) => prefetchInfoForKind(kind),
    { immediate: true },
  );

  return {
    isInfoDialog,
    isInterimResultsDialog,
    showInfo,
    viewResults,
    infoDialogHideHandler,
    interimResultsDialogHideHandler,
    isQuestionConcealed,
  };
}
